import {
  isImageArchiveInProgress,
  prescriptionRegistrationService,
  type FinalizedPrescriptionExcel,
  type ImageArchiveJob,
  type PrescriptionExportRequest,
  type PrescriptionRegistrationDetailParams,
  type PrescriptionRegistrationListParams,
  type RegistrationPeriod,
} from '@/entities/prescription-registration/api/prescription-registration.api';
import { LIST_STALE_TIME } from '@/shared/constants/query';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

/** 압축 작업 상태 조회 간격 (1~2초 권장) */
export const IMAGE_ARCHIVE_POLL_INTERVAL = 1500;

export const prescriptionRegistrationKeys = {
  all: ['partner-prescription-registrations'] as const,
  list: (params: PrescriptionRegistrationListParams) =>
    [...prescriptionRegistrationKeys.all, 'list', params] as const,
  summary: (period: Partial<RegistrationPeriod> | undefined) =>
    [...prescriptionRegistrationKeys.all, 'summary', period ?? null] as const,
  months: (registrationYear: string) =>
    [...prescriptionRegistrationKeys.all, 'months', registrationYear] as const,
  detail: (params: PrescriptionRegistrationDetailParams) =>
    [...prescriptionRegistrationKeys.all, 'detail', params] as const,
  imageArchiveJob: (jobId: string | null) =>
    [...prescriptionRegistrationKeys.all, 'image-archive', jobId] as const,
};

/** 07 처방 등록 현황 목록. page는 0부터. */
export const usePrescriptionRegistrationList = (
  params: PrescriptionRegistrationListParams,
  { enabled = true }: { enabled?: boolean } = {},
) =>
  useQuery({
    queryKey: prescriptionRegistrationKeys.list(params),
    queryFn: () => prescriptionRegistrationService.list(params),
    placeholderData: keepPreviousData,
    staleTime: LIST_STALE_TIME,
    enabled,
  });

/**
 * 07 상단 요약 카드 — 년월 조합이 바뀔 때만 다시 조회한다(검색·필터·정렬과 무관).
 * 목록과 숫자가 어긋나지 않게 같은 staleTime.
 */
export const usePrescriptionRegistrationSummary = (
  period: Partial<RegistrationPeriod> | undefined,
) =>
  useQuery({
    queryKey: prescriptionRegistrationKeys.summary(period),
    queryFn: () => prescriptionRegistrationService.summary(period),
    staleTime: LIST_STALE_TIME,
    enabled: period !== undefined,
  });

/** 07 처방 등록 년월 선택지 — 고른 등록 년도(yyyy)의 조합, 서버가 준 순서(최신순) 그대로 */
export const usePrescriptionRegistrationMonths = (registrationYear: string) =>
  useQuery({
    queryKey: prescriptionRegistrationKeys.months(registrationYear),
    queryFn: () => prescriptionRegistrationService.months(registrationYear),
    staleTime: LIST_STALE_TIME,
  });

/** 07-1 상세 패널 — 패널이 열려 있을 때(params 있음)만 조회 */
export const usePrescriptionRegistrationDetail = (
  params: PrescriptionRegistrationDetailParams | null,
) =>
  useQuery({
    queryKey: prescriptionRegistrationKeys.detail(
      params ?? { userId: 0, clinicId: 0 },
    ),
    queryFn: () => prescriptionRegistrationService.detail(params!),
    enabled: params != null,
  });

interface ImageArchiveHandlers {
  /** COMPLETED — downloadUrl 이 있다 */
  onCompleted: (job: ImageArchiveJob) => void;
  /** FAILED · EXPIRED */
  onFailed: (job: ImageArchiveJob) => void;
  /** 압축 요청·상태 조회 자체가 실패 (400·404·503·네트워크) */
  onError: (error: unknown) => void;
}

/**
 * 처방 등록 이미지 ZIP — 작업 생성(POST) → 끝날 때까지 상태 조회(GET) → 결과를 한 번 알린다.
 * 압축 중(isRunning)에는 다시 요청하지 않는다. 서버도 같은 조건의 진행 중 작업은 재사용한다.
 */
export const useImageArchiveDownload = (handlers: ImageArchiveHandlers) => {
  const [jobId, setJobId] = useState<string | null>(null);
  // 렌더마다 새 콜백이 와도 마지막 것을 부른다
  const handlersRef = useRef(handlers);
  useEffect(() => {
    handlersRef.current = handlers;
  });

  const request = useMutation({
    mutationFn: prescriptionRegistrationService.requestImageArchive,
    onSuccess: (job) => setJobId(job.jobId),
    onError: (error) => handlersRef.current.onError(error),
  });

  const job = useQuery({
    queryKey: prescriptionRegistrationKeys.imageArchiveJob(jobId),
    queryFn: () => prescriptionRegistrationService.imageArchiveJob(jobId!),
    enabled: jobId != null,
    refetchInterval: (query) =>
      query.state.data && !isImageArchiveInProgress(query.state.data.status)
        ? false
        : IMAGE_ARCHIVE_POLL_INTERVAL,
    // 다운로드 URL 은 조회할 때마다 새로 발급된다 — 캐시를 남기지 않는다
    gcTime: 0,
    retry: false,
  });

  useEffect(() => {
    if (jobId == null) return;
    if (job.isError) {
      setJobId(null);
      handlersRef.current.onError(job.error);
      return;
    }
    if (!job.data || isImageArchiveInProgress(job.data.status)) return;
    setJobId(null);
    if (job.data.status === 'COMPLETED')
      handlersRef.current.onCompleted(job.data);
    else handlersRef.current.onFailed(job.data);
  }, [jobId, job.data, job.isError, job.error]);

  return {
    /** 작업이 만들어지면 끝난다(압축 완료가 아니다). 요청이 실패하면 onError 를 부른 뒤 reject */
    start: async (body: PrescriptionExportRequest) => {
      if (request.isPending || jobId != null) return;
      await request.mutateAsync(body);
    },
    isRunning: request.isPending || jobId != null,
    /** 진행 중인 작업 (요청 직후·첫 조회 전에는 없음) */
    job: jobId != null ? job.data : undefined,
  };
};

/** 확정 처방 내역 엑셀 — 동기 생성, 응답의 downloadUrl 로 바로 받는다 */
export const useFinalizedPrescriptionExcel = (handlers: {
  onSuccess: (excel: FinalizedPrescriptionExcel) => void;
  onError: (error: unknown) => void;
}) =>
  useMutation({
    mutationFn: prescriptionRegistrationService.finalizedExcel,
    onSuccess: handlers.onSuccess,
    onError: handlers.onError,
  });
